import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Recipes from './pages/Recipes';
import RecipeDetail from './pages/RecipeDetail';
import Ingredients from './pages/Ingredients';
import ShoppingList from './pages/ShoppingList';
import UserManagement from './pages/UserManagement';
import ActivityLogs from './pages/ActivityLogs';
import MakeRecipe from './pages/MakeRecipe';
import HPP from './pages/HPP';
import Cashier from './pages/Cashier';
import FinancialReport from './pages/FinancialReport';
import PageTransition from './components/PageTransition';
import { AnimatePresence } from 'motion/react';

const ProtectedRoute = ({ 
  children, 
  allowedRoles = ['admin', 'staff', 'kasir', 'custom'],
  path
}: { 
  children: React.ReactNode, 
  allowedRoles?: ('admin' | 'staff' | 'kasir' | 'custom')[],
  path?: string
}) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return null; // Let Layout handle the login screen
  
  if (user.role === 'custom' && path) {
    if (!user.allowedPages?.includes(path)) {
      // Find first allowed page or go home
      const firstPage = user.allowedPages?.[0] || "/";
      return <Navigate to={firstPage} />;
    }
    return <>{children}</>;
  }

  if (!allowedRoles.includes(user.role)) {
    if (user.role === 'staff') return <Navigate to="/make-recipe" replace />;
    if (user.role === 'kasir') return <Navigate to="/cashier" replace />;
    if (user.role === 'custom') return <Navigate to={user.allowedPages?.[0] || "/"} replace />;
    return <Navigate to="/recipes" replace />;
  }

  return <>{children}</>;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location}>
        <Route 
          path="/" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/">
              <PageTransition>
                <Dashboard />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recipes" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/recipes">
              <PageTransition>
                <Recipes />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/recipes/:id" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/recipes">
              <PageTransition>
                <RecipeDetail />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/make-recipe" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'staff', 'custom']} path="/make-recipe">
              <PageTransition>
                <MakeRecipe />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/hpp" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/hpp">
              <PageTransition>
                <HPP />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/cashier" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'kasir', 'custom']} path="/cashier">
              <PageTransition>
                <Cashier />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/financial-report" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/financial-report">
              <PageTransition>
                <FinancialReport />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/ingredients" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/ingredients">
              <PageTransition>
                <Ingredients />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/shopping-list" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/shopping-list">
              <PageTransition>
                <ShoppingList />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/users" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/users">
              <PageTransition>
                <UserManagement />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/activity-logs" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'custom']} path="/activity-logs">
              <PageTransition>
                <ActivityLogs />
              </PageTransition>
            </ProtectedRoute>
          } 
        />
      </Routes>
    </AnimatePresence>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Layout>
          <AnimatedRoutes />
        </Layout>
      </Router>
    </AuthProvider>
  );
}
